import { Grid, LinearProgress, Stack } from "@mui/material";
import noop from "lodash/noop";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ProductResponse } from "../admin/products/productsApi";
import { useProducts } from "../admin/products/useProducts";
import { useAuthContext } from "../auth/AuthContext";
import type { UserContext } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledSwitch from "../components/labelledSwitch/LabelledSwitch";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import { findSaveDiscrepancies, type SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import CalculationCancelButton from "./CalculationCancelButton";
import CalculationVersion from "./CalculationVersion";
import CustomerSelect from "../components/customerSelect/CustomerSelect";
import { areProductsAndCalculationCurrenciesEqual } from "./areProductsAndCalculationCurrenciesEqual";
import type { CalculationVersion as CalculationVersionType } from "./calculationTypes";
import { initializeCalculationFromOrder, initializeCalculationFromPackage } from "./calculationUtils";
import CalculationVersionEdit from "./calculationVersionEdit/CalculationVersionEdit";
import { useCalculationFromQuery } from "./useCalculationFromQuery";
import { useCreateCalculation } from "./useCreateCalculation";

export type NewCalculation = CalculationVersionType & { customerId: string; usePackagePrices: boolean };

const defaultCalculation: (customerId: string | undefined) => NewCalculation = (customerId) => ({
  customerId: customerId ?? "",
  usePackagePrices: false,
  name: "",
  participantsNumber: { numberOfPaid: 1, numberOfStaff: 0 },
  packageId: undefined,
  currency: undefined,
  products: [],
  startDate: dayjs().add(1, "day").format(ISO_DATE_FORMAT),
});

interface NewCalculationDetailsProps {
  readonly context: UserContext;
  readonly products: ProductResponse[];
  readonly initialCalculation: NewCalculation;
}

export const NewCalculationDetails: FC<NewCalculationDetailsProps> = ({ context, products, initialCalculation }) => {
  const navigate = useNavigate();
  const [calculation, setCalculation] = useState(initialCalculation);
  const [currencyError, setCurrencyError] = useState(false);
  const [currencyErrors, setCurrencyErrors] = useState<string[]>([]);
  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);
  const { onCreate } = useCreateCalculation();

  const navigateToCalculation = (id: string) => navigate(`/calculations/${id}`);

  const getProductCurrency = (productId: string) => products?.find((p) => p.id === productId)?.currency;

  const doCreate = async () => {
    const { packageId, currency, margin, usePackagePrices, ...otherCalculationProps } = calculation;
    const { id } = await onCreate(
      {
        ...otherCalculationProps,
        packageId,
        currency: packageId ? undefined : currency,
        margin: packageId ? undefined : margin,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        products: calculation.products.map(({ daysShift, ...p }) => ({
          ...p,
          options: p.options.map((o) => ({ id: o.id, quantity: o.quantity, price: o.price ?? undefined })),
        })),
        type: usePackagePrices ? "PACKAGE" : "DEFAULT",
      },
      context.role,
    );
    navigateToCalculation(id);
  };

  const handleSubmit = async () => {
    const currenciesAreEqual = areProductsAndCalculationCurrenciesEqual(calculation, products);
    if (!currenciesAreEqual) {
      setCurrencyError(true);
      return;
    }
    const { packageId } = calculation;
    const productsWithDifferentCurrency = packageId
      ? []
      : calculation.products.map((p) => p.id).filter((id) => getProductCurrency(id) !== calculation.currency);
    if (productsWithDifferentCurrency.length > 0) {
      setCurrencyErrors(productsWithDifferentCurrency);
      return;
    }
    const totalParticipants =
      calculation.participantsNumber.numberOfPaid + calculation.participantsNumber.numberOfStaff;
    const discrepancies = findSaveDiscrepancies(totalParticipants, calculation.products, products);
    if (discrepancies) {
      setDiscrepancyModal(discrepancies);
      return;
    }
    await doCreate();
  };

  const handleDiscrepancyConfirm = async () => {
    await doCreate();
    setDiscrepancyModal(null);
  };

  const handleDiscrepancyCancel = () => {
    setDiscrepancyModal(null);
  };

  const handleChange = (change: Partial<NewCalculation>) => {
    setCalculation((c) => ({ ...c, ...change }));
    setCurrencyErrors([]);
    setCurrencyError(false);
  };

  const saveDisabled = !calculation.customerId || !calculation.name || calculation.products.length <= 0;

  const isNotCustomer = context?.role !== "CUSTOMER";
  return (
    <>
      {isNotCustomer && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <CustomerSelect
            customerId={calculation.customerId}
            showLink
            disableClearable
            onChange={(customerId) => handleChange({ customerId: customerId ?? "" })}
          />
        </Grid>
      )}
      <Grid size={{ xs: isNotCustomer ? 6 : 12, lg: isNotCustomer ? 4 : 6 }}>
        <CalculationVersion
          selectedIdx={0}
          numberOfSavedVersions={0}
          onVersionChange={noop}
          versions={[calculation]}
          onVersionNameChange={(versionName) => handleChange({ versionName: versionName ?? undefined })}
        />
      </Grid>
      {isNotCustomer && (
        <Grid size={{ xs: 6, lg: 2 }}>
          <LabelledSwitch
            label="Cena pakietowa"
            checked={calculation.usePackagePrices}
            onChange={(v) => handleChange({ usePackagePrices: v })}
          />
        </Grid>
      )}
      <Grid size={12}>
        <CalculationVersionEdit
          calculationVersion={calculation}
          userContext={context}
          productCurrencyErrors={currencyErrors}
          currencyError={currencyError}
          onChange={(v) => handleChange(v)}
        />
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <CalculationCancelButton />
          <AsyncLoadingButton disabled={saveDisabled} onClick={handleSubmit}>
            Generuj kalkulację
          </AsyncLoadingButton>
        </Stack>
      </Grid>
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        onConfirm={handleDiscrepancyConfirm}
        onCancel={handleDiscrepancyCancel}
      />
    </>
  );
};

const NewCalculationPage = () => {
  const { context } = useAuthContext();
  const { type, isLoading: isFromLoading, data } = useCalculationFromQuery();
  const { products, isLoading: areProductsLoading } = useProducts();

  const isLoading = isFromLoading || areProductsLoading || !context;
  let calculation = defaultCalculation(context?.role === "CUSTOMER" ? context.customerId : undefined);
  if (type === "FROM_ORDER") {
    calculation = { ...calculation, ...initializeCalculationFromOrder(data) } as NewCalculation;
  }
  if (type === "FROM_PACKAGE") {
    calculation = {
      ...calculation,
      ...initializeCalculationFromPackage(data, dayjs(calculation.startDate, ISO_DATE_FORMAT)),
    } as NewCalculation;
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      {isLoading ? (
        <Grid size={12}>
          <LinearProgress />
        </Grid>
      ) : (
        <NewCalculationDetails context={context} products={products} initialCalculation={calculation} />
      )}
    </Grid>
  );
};

export default NewCalculationPage;
